









































































































































import { useExecutionErrors } from '@/app/composable';
import { ExclamationIcon, XIcon } from '@vue-hero-icons/outline';
import { PropType, computed, defineComponent, ref, watch } from '@vue/composition-api';
import Draggable from 'vuedraggable';
import { Stats, StatsPerField } from '../../types';
import FieldPath from '../FieldPath.vue';
import PredictionScore from './PredictionScore.vue';
import { isNil } from 'ramda';

const Dragster = require('@/app/assets/js/dragster.js'); // eslint-disable-line @typescript-eslint/no-var-requires

export default defineComponent({
    name: 'ConceptView',
    components: { PredictionScore, Draggable, FieldPath, ExclamationIcon, XIcon },
    props: {
        concept: {
            type: Object,
            required: true,
        },
        selected: {
            type: Boolean,
            default: false,
        },
        latestExecutionStats: {
            type: Object as PropType<Stats<StatsPerField>>,
            required: false,
        },
        validationErrors: {
            type: Object as PropType<
                Record<number, { message: string | null; description?: string | null; type?: string; title?: string }>
            >,
            required: true,
        },
        basePath: {
            type: Array as PropType<string[]>,
            required: false,
            default: () => [],
        },
    },
    setup(props, { emit }) {
        const dragActive = ref<boolean>(false);
        const inDropZone = ref(false);
        const dropAreaRef = ref<any>(null);
        const droppedConcept = ref<any>([]);

        const isMacOS = window.navigator.userAgent.indexOf('Mac OS') !== -1;

        const { errorMessage } = useExecutionErrors();

        const failedTransformationReason = computed(() => {
            const errorCode = props?.latestExecutionStats?.statsPerField?.find((s) => s.id === props.concept.source.id)
                ?.errorCode;
            return errorCode ? errorMessage(errorCode).error.title : null;
        });

        const classes = computed(() => {
            if ((props.concept.temp && props.concept.temp.invalid) || failedTransformationReason.value) {
                if (props.selected) return 'rounded-r-md border-primary-700 bg-red-50';
                return 'rounded-md bg-red-50 border-red-600';
            }
            if (props.selected) return 'rounded-r-md border-primary-700';
            return 'rounded-md border-white';
        });

        const fullTargetPath = computed(() => {
            const path: Array<string> = [...props.concept.target.path];
            if (props.concept.target.title) path.push(props.concept.target.title);
            return path.join(' > ');
        });

        const fullSourcePath = computed(() => {
            const path: Array<string> = [...props.concept.source.path];
            if (props.concept.source.title) path.push(props.concept.source.title);
            return path.join(' > ');
        });

        const sameSourceTargetType = computed(() => {
            let targetType = props.concept.target.type;
            if (['double', 'integer', 'float'].includes(targetType)) targetType = 'number';
            return targetType === props.concept.source.type;
        });

        const conceptClicked = (event: MouseEvent) => {
            emit('toggle-selection', (event.metaKey && isMacOS) || (event.ctrlKey && !isMacOS));
        };

        const add = () => {
            if (!inDropZone.value) droppedConcept.value.splice(0);

            if (droppedConcept.value.length > 0) {
                emit('concept-added', droppedConcept.value[0]);
                droppedConcept.value.splice(0); // Clear drop area
            }

            inDropZone.value = false;
        };

        const handleDragEnter = () => {
            inDropZone.value = true;
        };

        const handleDragLeave = () => {
            inDropZone.value = false;
        };

        // Adding and removing drag handlers to correctly monitor when a field is dragged inside the designated area
        // Dragster is used to provide custom drag events that ignore child elements from triggering the
        // dragleave event, making the whole process problematic
        let dragster: any = null;
        watch(dropAreaRef, (current: any, prev: any) => {
            if (current && !prev) {
                dragster = new Dragster(current.$el);
                current.$el.addEventListener('dragster:enter', handleDragEnter, false);
                current.$el.addEventListener('dragster:leave', handleDragLeave, false);
            }

            if (prev && !current && dragster) {
                dragster.removeListeners();
                dragster = null;
            }
        });

        return {
            add,
            classes,
            droppedConcept,
            dragActive,
            dropAreaRef,
            inDropZone,
            conceptClicked,
            fullTargetPath,
            fullSourcePath,
            failedTransformationReason,
            sameSourceTargetType,
            isNil,
        };
    },
});
